/**
 * Extract the first H1 title from markdown content
 * @param {string} content - The markdown content
 * @returns {string|null} - The extracted title or null if not found
 */
export function extractH1Title(content) {
  if (!content || typeof content !== "string") {
    return null;
  }

  // Match H1 patterns: # Title or === underline format
  const h1Patterns = [
    /^#\s+(.+)$/m, // # Title
    /^(.+)\n=+$/m, // Title with === underline
  ];

  for (const pattern of h1Patterns) {
    const match = content.match(pattern);
    if (match && match[1]) {
      return match[1].trim();
    }
  }

  // Fallback: look for any line that looks like a title at the beginning
  const lines = content.split("\n").filter((line) => line.trim());
  if (lines.length > 0) {
    const firstLine = lines[0].trim();
    // If first line doesn't contain common markdown or formatting, treat as title
    if (
      firstLine &&
      !firstLine.startsWith("*") &&
      !firstLine.startsWith("-") &&
      !firstLine.startsWith("•") &&
      !firstLine.includes("**") &&
      firstLine.length < 100
    ) {
      // Reasonable title length
      return firstLine;
    }
  }

  return null;
}

/**
 * Generate a default title for deep research reports
 * @param {string} query - The original research query
 * @returns {string} - A formatted title
 */
export function generateDeepResearchTitle(query) {
  if (!query) {
    return "Deep Research Report";
  }

  // Clean up the query and make it title-case
  const cleanQuery = query
    .replace(/[^\w\s]/g, " ") // Remove special characters
    .replace(/\s+/g, " ") // Normalize whitespace
    .trim();

  // Truncate if too long
  if (cleanQuery.length > 60) {
    return cleanQuery.substring(0, 60).trim() + "...";
  }

  return cleanQuery;
}

/**
 * Generate a default title for canvas from user query
 * @param {string} query - The user's query that triggered canvas creation
 * @returns {string} - A formatted canvas title
 */
export function generateCanvasTitle(query) {
  if (!query) {
    return "Canvas";
  }

  // Clean up the query and make it title-case
  // Remove common canvas-related phrases
  let cleanQuery = query
    .replace(/create\s+canvas\s*(for|with|about)?\s*/i, "")
    .replace(/canvas\s*(mode|with|for|about)?\s*/i, "")
    .replace(
      /write\s+(a|an)?\s*(paragraph|document|article|code|text|content)\s*(about|on|for)?\s*/i,
      ""
    )
    .replace(/generate\s+(a|an)?\s*/i, "")
    .replace(/make\s+(a|an)?\s*/i, "")
    .replace(/[^\w\s]/g, " ") // Remove special characters
    .replace(/\s+/g, " ") // Normalize whitespace
    .trim();

  // Capitalize first letter of each word
  cleanQuery = cleanQuery
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

  // Truncate if too long
  if (cleanQuery.length > 60) {
    return cleanQuery.substring(0, 60).trim() + "...";
  }

  // If query is empty after cleaning, use default
  if (!cleanQuery || cleanQuery.trim().length === 0) {
    return "Canvas";
  }

  return cleanQuery;
}
